
import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';

export interface PlanFeatures {
  dashboard: boolean;
  clients: boolean;
  workflow: boolean;
  posts: boolean;
  approvals: boolean;
  calendar: boolean;
  reports: boolean;
  finances: boolean;
  mental_map: boolean;
  automations: boolean;
}

export type FeatureName = keyof PlanFeatures;

export function useFeatureAccess() {
  const { user } = useAuth();
  const [userPlan, setUserPlan] = useState<string>('basic');
  const [features, setFeatures] = useState<PlanFeatures>({
    dashboard: true,
    clients: true,
    workflow: true,
    posts: true,
    approvals: true,
    calendar: true, // Calendar is now available in all plans
    reports: false,
    finances: true,
    mental_map: false,
    automations: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlanFeatures = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Get user plan type
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('plan_type')
          .eq('id', user.id)
          .single();

        if (userError) {
          console.error('User error:', userError);
          // Set default to basic plan instead of throwing error
          setUserPlan('basic');
        } else {
          const planType = userData?.plan_type || 'basic';
          setUserPlan(planType);
        }

        // Get features for this plan
        const { data: planData, error: planError } = await supabase
          .from('plans')
          .select('features')
          .eq('name', userData?.plan_type || 'basic')
          .single();

        if (planError) {
          console.error('Plan error:', planError);
          // Use default features instead of throwing error
        } else if (planData) {
          // Fixing type conversion issue
          const rawFeatures = planData.features as Record<string, boolean>;
          const typedFeatures: PlanFeatures = {
            dashboard: Boolean(rawFeatures.dashboard),
            clients: Boolean(rawFeatures.clients), 
            workflow: Boolean(rawFeatures.workflow),
            posts: Boolean(rawFeatures.posts),
            approvals: Boolean(rawFeatures.approvals),
            calendar: true, // Always true regardless of plan settings from database
            reports: Boolean(rawFeatures.reports),
            finances: Boolean(rawFeatures.finances),
            mental_map: Boolean(rawFeatures.mental_map),
            automations: Boolean(rawFeatures.automations)
          };
          
          setFeatures(typedFeatures);
        }
      } catch (error) {
        console.error('Error fetching plan features:', error);
        // Silent error - use default features
      } finally {
        setLoading(false);
      }
    };

    fetchPlanFeatures();
  }, [user]);

  const hasAccess = (feature: FeatureName): boolean => {
    return features[feature] || false;
  };

  const updateUserPlan = async (newPlan: 'basic' | 'business' | 'advanced') => {
    if (!user) return false;
    
    try {
      const { error } = await supabase
        .from('users')
        .update({ plan_type: newPlan })
        .eq('id', user.id);
        
      if (error) throw error;
      
      // Update local state
      setUserPlan(newPlan);
      
      // Fetch updated features
      const { data, error: planError } = await supabase
        .from('plans')
        .select('features')
        .eq('name', newPlan)
        .single();
        
      if (planError) throw planError;
      
      // Fixing type conversion issue
      const rawFeatures = data.features as Record<string, boolean>;
      const typedFeatures: PlanFeatures = {
        dashboard: Boolean(rawFeatures.dashboard),
        clients: Boolean(rawFeatures.clients), 
        workflow: Boolean(rawFeatures.workflow),
        posts: Boolean(rawFeatures.posts),
        approvals: Boolean(rawFeatures.approvals),
        calendar: Boolean(rawFeatures.calendar),
        reports: Boolean(rawFeatures.reports),
        finances: Boolean(rawFeatures.finances),
        mental_map: Boolean(rawFeatures.mental_map),
        automations: Boolean(rawFeatures.automations)
      };
      
      setFeatures(typedFeatures);
      return true;
    } catch (error) {
      console.error('Error updating plan:', error);
      return false;
    }
  };

  return {
    hasAccess,
    userPlan,
    updateUserPlan,
    loading,
    features
  };
}
